import React, { useEffect, useMemo, useState } from "react";
import { Alert, Box, Divider, Skeleton, Stack, Typography } from "@mui/material";
import AirRoundedIcon from "@mui/icons-material/AirRounded";
import WaterDropRoundedIcon from "@mui/icons-material/WaterDropRounded";
import ThermostatRoundedIcon from "@mui/icons-material/ThermostatRounded";

import { StayDetail } from "../../services/restserver-openapi";
import { fetchLocationWeather, getWeatherPresentation, OpenMeteoWeather } from "../../utils/Vanlifer/weather";

type TourWeatherTabProps = {
    stays?: StayDetail[];
};

type WeatherLocation = {
    latitude?: number;
    longitude?: number;
    city: string;
};

type ForecastEntry = {
    day: string;
    weatherCode: number;
    min: number;
    max: number;
    rain: number;
};

function formatNumber(value?: number, unit?: string): string {
    if (value == null || Number.isNaN(value)) {
        return "-";
    }

    return `${Math.round(value)}${unit ?? ""}`;
}

function formatForecastDay(value?: string): string {
    if (!value) {
        return "-";
    }

    const date = new Date(value);
    return new Intl.DateTimeFormat("de-CH", { weekday: "short" }).format(date);
}

function buildForecastDays(weather?: OpenMeteoWeather | null): ForecastEntry[] {
    const daily = weather?.daily;
    if (!daily) {
        return [];
    }

    return daily.time.slice(1, 4).map((day, index) => ({
        day,
        weatherCode: daily.weather_code[index + 1],
        min: daily.temperature_2m_min[index + 1],
        max: daily.temperature_2m_max[index + 1],
        rain: daily.precipitation_probability_max[index + 1],
    }));
}

export default function TourWeatherTab({ stays = [] }: TourWeatherTabProps) {
    const [startWeather, setStartWeather] = useState<OpenMeteoWeather | null>(null);
    const [targetWeather, setTargetWeather] = useState<OpenMeteoWeather | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);

    const hostStays = useMemo(
        () => stays.filter((stay) => stay.stayType === 'HOST'),
        [stays]
    );

    const startStay = useMemo(() => {
        if (hostStays.length === 0) {
            return undefined;
        }

        return [...hostStays].sort((a, b) => (a.fromDate ?? 0) - (b.fromDate ?? 0))[0];
    }, [hostStays]);

    const targetStay = useMemo(() => {
        if (hostStays.length === 0) {
            return undefined;
        }

        return [...hostStays].sort((a, b) => (b.toDate ?? 0) - (a.toDate ?? 0))[0];
    }, [hostStays]);

    const startLocation = useMemo<WeatherLocation>(() => ({
        latitude: startStay?.stayCompound?.coordinates?.x,
        longitude: startStay?.stayCompound?.coordinates?.y,
        city: startStay?.stayCompound?.city ?? startStay?.stayCompound?.name ?? "Unbekannter Startort",
    }), [startStay]);

    const targetLocation = useMemo<WeatherLocation>(() => ({
        latitude: targetStay?.stayCompound?.coordinates?.x,
        longitude: targetStay?.stayCompound?.coordinates?.y,
        city: targetStay?.stayCompound?.city ?? targetStay?.stayCompound?.name ?? "Unbekannter Zielort",
    }), [targetStay]);

    useEffect(() => {
        let isCancelled = false;

        const loadWeather = async () => {
            if (startLocation.latitude == null || startLocation.longitude == null || targetLocation.latitude == null || targetLocation.longitude == null) {
                setStartWeather(null);
                setTargetWeather(null);
                return;
            }

            try {
                setLoading(true);
                setError(false);
                const [startResponse, targetResponse] = await Promise.all([
                    fetchLocationWeather(startLocation.latitude, startLocation.longitude),
                    fetchLocationWeather(targetLocation.latitude, targetLocation.longitude),
                ]);
                if (!isCancelled) {
                    setStartWeather(startResponse);
                    setTargetWeather(targetResponse);
                }
            } catch (loadError) {
                if (!isCancelled) {
                    console.error("Loading weather failed", loadError);
                    setError(true);
                }
            } finally {
                if (!isCancelled) {
                    setLoading(false);
                }
            }
        };

        loadWeather();

        return () => {
            isCancelled = true;
        };
    }, [startLocation.latitude, startLocation.longitude, targetLocation.latitude, targetLocation.longitude]);

    const startCurrent = startWeather?.current;
    const startDaily = startWeather?.daily;
    const startPresentation = getWeatherPresentation(startCurrent?.weather_code);

    const targetCurrent = targetWeather?.current;
    const targetDaily = targetWeather?.daily;
    const targetPresentation = getWeatherPresentation(targetCurrent?.weather_code);
    const startForecastDays = buildForecastDays(startWeather);
    const targetForecastDays = buildForecastDays(targetWeather);

    if (startLocation.latitude == null || startLocation.longitude == null || targetLocation.latitude == null || targetLocation.longitude == null) {
        return <Alert severity="info">Für Start- oder Zielort sind noch keine Wetterdaten verfügbar.</Alert>;
    }

    if (loading) {
        return <Skeleton variant="rectangular" height={260} sx={{ borderRadius: 2 }} />;
    }

    if (error || !startCurrent || !startDaily || !targetCurrent || !targetDaily) {
        return <Alert severity="warning">Das Wetter konnte gerade nicht geladen werden.</Alert>;
    }

    return (
        <Box sx={{ py: 1 }}>
            <Stack spacing={2.5}>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="overline" sx={{ letterSpacing: 1.1, color: "text.secondary" }}>
                        Wetter am ersten Aufenthaltsort
                    </Typography>

                    <Stack direction={{ xs: "column", md: "row" }} spacing={3} justifyContent="space-between" sx={{ mt: 1.5 }}>
                        <Box>
                            <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                                {startLocation.city}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                Am Ort deines ersten Aufenthalts
                            </Typography>

                            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 3 }}>
                                <Typography sx={{ fontSize: 40, lineHeight: 1 }}>{startPresentation.icon}</Typography>
                                <Box>
                                    <Typography sx={{ fontSize: { xs: 40, sm: 48 }, fontWeight: 800, lineHeight: 0.95 }}>
                                        {formatNumber(startCurrent.temperature_2m, "°")}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 600 }}>
                                        {startPresentation.label}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Box>

                        <Stack spacing={1.25} sx={{ minWidth: { md: 260 } }}>
                            <Box>
                                <Stack direction="row" spacing={1.25} alignItems="center">
                                    <ThermostatRoundedIcon sx={{ color: startPresentation.accent }} />
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Heute</Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 700 }}>
                                            {formatNumber(startDaily.temperature_2m_min?.[0], "°")} bis {formatNumber(startDaily.temperature_2m_max?.[0], "°")}
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Box>

                            <Box>
                                <Stack direction="row" spacing={1.25} alignItems="center">
                                    <WaterDropRoundedIcon sx={{ color: startPresentation.accent }} />
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Regenchance</Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 700 }}>
                                            {formatNumber(startDaily.precipitation_probability_max?.[0], "%")}
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Box>

                            <Box>
                                <Stack direction="row" spacing={1.25} alignItems="center">
                                    <AirRoundedIcon sx={{ color: startPresentation.accent }} />
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Wind</Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 700 }}>
                                            {formatNumber(startCurrent.wind_speed_10m, " km/h")}
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Box>
                        </Stack>
                    </Stack>

                    {startForecastDays.length > 0 && (
                        <Box sx={{ mt: 1.75 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                                Vorschau fuer die naechsten Tage
                            </Typography>
                            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                                {startForecastDays.map((entry) => {
                                    const forecastPresentation = getWeatherPresentation(entry.weatherCode);

                                    return (
                                        <Box
                                            key={`start-${entry.day}`}
                                            sx={{
                                                flex: 1,
                                                minWidth: 0,
                                                py: 0.9,
                                                px: 1,
                                                border: "1px solid",
                                                borderColor: "divider",
                                                borderRadius: 2,
                                            }}
                                        >
                                            <Typography variant="caption" color="text.secondary">
                                                {formatForecastDay(entry.day)}
                                            </Typography>
                                            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.5 }}>
                                                <Typography sx={{ fontSize: 18, lineHeight: 1 }}>{forecastPresentation.icon}</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                    {formatNumber(entry.max, "°")}
                                                </Typography>
                                            </Stack>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.35 }}>
                                                min {formatNumber(entry.min, "°")} · Regen {formatNumber(entry.rain, "%")}
                                            </Typography>
                                        </Box>
                                    );
                                })}
                            </Stack>
                        </Box>
                    )}
                </Box>

                <Divider />

                <Box sx={{ flex: 1 }}>
                    <Typography variant="overline" sx={{ letterSpacing: 1.1, color: "text.secondary" }}>
                        Wetter am letzten Aufenthaltsort
                    </Typography>

                    <Stack direction={{ xs: "column", md: "row" }} spacing={3} justifyContent="space-between" sx={{ mt: 1.5 }}>
                        <Box>
                            <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                                {targetLocation.city}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                Am Ort deines letzten Aufenthalts
                            </Typography>

                            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 3 }}>
                                <Typography sx={{ fontSize: 40, lineHeight: 1 }}>{targetPresentation.icon}</Typography>
                                <Box>
                                    <Typography sx={{ fontSize: { xs: 40, sm: 48 }, fontWeight: 800, lineHeight: 0.95 }}>
                                        {formatNumber(targetCurrent.temperature_2m, "°")}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 600 }}>
                                        {targetPresentation.label}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Box>

                        <Stack spacing={1.25} sx={{ minWidth: { md: 260 } }}>
                            <Box>
                                <Stack direction="row" spacing={1.25} alignItems="center">
                                    <ThermostatRoundedIcon sx={{ color: targetPresentation.accent }} />
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Heute</Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 700 }}>
                                            {formatNumber(targetDaily.temperature_2m_min?.[0], "°")} bis {formatNumber(targetDaily.temperature_2m_max?.[0], "°")}
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Box>

                            <Box>
                                <Stack direction="row" spacing={1.25} alignItems="center">
                                    <WaterDropRoundedIcon sx={{ color: targetPresentation.accent }} />
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Regenchance</Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 700 }}>
                                            {formatNumber(targetDaily.precipitation_probability_max?.[0], "%")}
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Box>

                            <Box>
                                <Stack direction="row" spacing={1.25} alignItems="center">
                                    <AirRoundedIcon sx={{ color: targetPresentation.accent }} />
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Wind</Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 700 }}>
                                            {formatNumber(targetCurrent.wind_speed_10m, " km/h")}
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Box>
                        </Stack>
                    </Stack>
                    {targetForecastDays.length > 0 && (
                        <Box sx={{ mt: 1.75 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                                Vorschau fuer die naechsten Tage
                            </Typography>
                            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                                {targetForecastDays.map((entry) => {
                                    const forecastPresentation = getWeatherPresentation(entry.weatherCode);

                                    return (
                                        <Box
                                            key={`target-${entry.day}`}
                                            sx={{
                                                flex: 1,
                                                minWidth: 0,
                                                py: 0.9,
                                                px: 1,
                                                border: "1px solid",
                                                borderColor: "divider",
                                                borderRadius: 2,
                                            }}
                                        >
                                            <Typography variant="caption" color="text.secondary">
                                                {formatForecastDay(entry.day)}
                                            </Typography>
                                            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.5 }}>
                                                <Typography sx={{ fontSize: 18, lineHeight: 1 }}>{forecastPresentation.icon}</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                    {formatNumber(entry.max, "°")}
                                                </Typography>
                                            </Stack>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.35 }}>
                                                min {formatNumber(entry.min, "°")} · Regen {formatNumber(entry.rain, "%")}
                                            </Typography>
                                        </Box>
                                    );
                                })}
                            </Stack>
                        </Box>
                    )}
                </Box>
            </Stack>
        </Box>
    );
}
